import { useCallback, useEffect, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Area, Botao, Campo, Selecao } from '../componentes/forma.tsx';
import { Bancada, Busca, Iniciais, Lista, Painel } from '../componentes/painel.tsx';
import { ErroApi, query } from '../lib/api.ts';
import { useCadastro } from '../lib/cadastro.ts';
import {
  dataBr, formatarCep, formatarCpf, formatarTelefone, idade, soDigitos, UFS,
} from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';

interface Paciente {
  id: string;
  nome: string;
  cpf: string | null;
  rg: string | null;
  nascimento: string;
  sexo: 'M' | 'F' | 'O' | null;
  telefone: string | null;
  email: string | null;
  matricula: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  observacoes: string | null;
}

type Form = Omit<Paciente, 'id'>;

const VAZIO: Form = {
  nome: '', cpf: '', rg: '', nascimento: '', sexo: null, telefone: '', email: '',
  matricula: '', endereco: '', cidade: '', uf: '', cep: '', observacoes: '',
};

export function Pacientes() {
  const pode = usePode();
  const podeEditar = pode('pode_pacientes');

  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [letra, setLetra] = useState('');
  const [pagina, setPagina] = useState(1);

  // A busca dispara sozinha depois da pausa de digitação: na recepção a pessoa
  // digita e lê o resultado, sem apertar botão.
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAtiva(busca);
      setPagina(1);
    }, 260);
    return () => clearTimeout(t);
  }, [busca]);

  const paraFormulario = useCallback((p: Paciente) => {
    const { id: _id, ...resto } = p;
    return resto;
  }, []);

  const c = useCadastro<Paciente, Form>({
    recurso: 'pacientes',
    vazio: VAZIO,
    paraFormulario,
    rotulo: (p) => p.nome,
    consulta: query({ busca: buscaAtiva, letra, pagina }),
  });

  const dados = c.lista.data as
    | { itens: Paciente[]; total: number; porPagina?: number; iniciais?: string[] }
    | undefined;
  const porPagina = dados?.porPagina ?? 25;
  const paginas = Math.max(1, Math.ceil(c.total / porPagina));

  return (
    <Layout>
      <Cabecalho
        titulo="Pacientes"
        contagem={c.lista.isPending ? '' : `${c.total} ${c.total === 1 ? 'paciente' : 'pacientes'}`}
      >
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Novo paciente
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? 'Editar paciente' : 'Novo paciente'}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={c.salvo}
          salvando={c.salvando}
          rotuloCriar="Cadastrar paciente"
          aoEnviar={c.enviar}
          aoCancelar={c.limpar}
          somenteLeitura={!podeEditar}
          excluindo={c.excluir.isPending}
          aoExcluir={() => {
            if (confirm(`Excluir ${c.form.nome}? Isso não pode ser desfeito.`)) {
              c.excluir.mutate(c.editando!);
            }
          }}
        >
          <Campo
            rotulo="Nome completo"
            obrigatorio
            autoComplete="off"
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="Nascimento"
            type="date"
            largura="meia"
            obrigatorio
            value={c.form.nascimento}
            erro={c.campos.nascimento}
            onChange={c.mudar('nascimento')}
            disabled={!podeEditar}
            dica={c.form.nascimento ? `${idade(c.form.nascimento)} anos` : undefined}
          />
          <Selecao
            rotulo="Sexo"
            largura="meia"
            value={c.form.sexo ?? ''}
            erro={c.campos.sexo}
            onChange={(e) => c.definir('sexo', (e.target.value || null) as Form['sexo'])}
            disabled={!podeEditar}
          >
            <option value="">—</option>
            <option value="F">Feminino</option>
            <option value="M">Masculino</option>
            <option value="O">Outro</option>
          </Selecao>

          <Campo
            rotulo="CPF"
            largura="meia"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={formatarCpf(c.form.cpf)}
            erro={c.campos.cpf}
            onChange={c.mudar('cpf', soDigitos)}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="RG"
            largura="meia"
            value={c.form.rg ?? ''}
            erro={c.campos.rg}
            onChange={c.mudar('rg')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="Telefone"
            largura="meia"
            inputMode="numeric"
            placeholder="(31) 90000-0000"
            value={formatarTelefone(c.form.telefone)}
            erro={c.campos.telefone}
            onChange={c.mudar('telefone', soDigitos)}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="Matrícula"
            largura="meia"
            dica="Código do funcionário na empresa"
            value={c.form.matricula ?? ''}
            erro={c.campos.matricula}
            onChange={c.mudar('matricula')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="E-mail"
            type="email"
            value={c.form.email ?? ''}
            erro={c.campos.email}
            onChange={c.mudar('email')}
            disabled={!podeEditar}
          />
          <Campo
            rotulo="Endereço"
            value={c.form.endereco ?? ''}
            erro={c.campos.endereco}
            onChange={c.mudar('endereco')}
            disabled={!podeEditar}
          />

          <Campo
            rotulo="Cidade"
            largura="meia"
            value={c.form.cidade ?? ''}
            erro={c.campos.cidade}
            onChange={c.mudar('cidade')}
            disabled={!podeEditar}
          />
          <Selecao
            rotulo="UF"
            largura="curta"
            value={c.form.uf ?? ''}
            erro={c.campos.uf}
            onChange={c.mudar('uf')}
            disabled={!podeEditar}
          >
            <option value="">—</option>
            {UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </Selecao>
          <Campo
            rotulo="CEP"
            largura="curta"
            inputMode="numeric"
            placeholder="00000-000"
            value={formatarCep(c.form.cep)}
            erro={c.campos.cep}
            onChange={c.mudar('cep', soDigitos)}
            disabled={!podeEditar}
          />

          <Area
            rotulo="Observações"
            rows={2}
            value={c.form.observacoes ?? ''}
            onChange={c.mudar('observacoes')}
            disabled={!podeEditar}
          />
        </Painel>

        <div>
          <div className="filtros cartao">
            <Busca
              valor={busca}
              aoMudar={setBusca}
              placeholder="Nome, CPF, matrícula ou data de nascimento"
            />
            <Iniciais
              disponiveis={dados?.iniciais ?? []}
              atual={letra}
              aoEscolher={(l) => {
                setLetra(l);
                setPagina(1);
              }}
            />
          </div>

          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              c.total === 0
                ? {
                    titulo:
                      buscaAtiva || letra ? 'Nenhum paciente encontrado' : 'Nenhum paciente cadastrado',
                    apoio:
                      buscaAtiva || letra
                        ? 'Ajuste a busca ou limpe o filtro de inicial.'
                        : 'Preencha o formulário ao lado para cadastrar o primeiro.',
                  }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className="tabela--num">Nascimento</th>
                  <th className="tabela--num">Idade</th>
                  <th className="tabela--num">CPF</th>
                  <th className="tabela--num">Telefone</th>
                  <th>Matrícula</th>
                </tr>
              </thead>
              <tbody>
                {c.itens.map((p) => (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === p.id || undefined}
                    onClick={() => c.selecionar(p)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        c.selecionar(p);
                      }
                    }}
                  >
                    <td className="tabela__nome">{p.nome}</td>
                    <td className="tabela--num mono">{dataBr(p.nascimento)}</td>
                    <td className="tabela--num num">{idade(p.nascimento)}</td>
                    <td className="tabela--num mono">{formatarCpf(p.cpf) || '—'}</td>
                    <td className="tabela--num mono">{formatarTelefone(p.telefone) || '—'}</td>
                    <td className="mono">{p.matricula || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>

          {paginas > 1 && (
            <nav className="paginacao" aria-label="Paginação">
              <Botao tamanho="sm" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}>
                Anterior
              </Botao>
              <span className="paginacao__info num">
                Página {pagina} de {paginas}
              </span>
              <Botao tamanho="sm" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
                Próxima
              </Botao>
            </nav>
          )}
        </div>
      </Bancada>
    </Layout>
  );
}
